/* eslint-disable camelcase */
/**
 * Zod codec for `tool_results` widget envelopes — Phase 1 Slice A of
 * the wire-codec boundary migration.
 *
 * Sibling to `tool-cards/schemas.ts` (which owns `card_kind` orchestrator
 * cards). This file owns the legacy tool-result widget envelope:
 *
 *     { render_kind: "data_grid" | "key_value" | ..., payload: {...}, tool_name?: string }
 *
 * Same dual-transport problem the module-level CTO note in `api/base.ts`
 * describes: a tool-result lands snake_case on the SSE path
 * (`/api/chat/turns/stream/` → `done.tool_results`) and camelCase on the
 * replay path (`/api/chat/threads/<id>/messages/` → the axios interceptor
 * recursively camelizes the JSONField, so `render_kind` becomes
 * `renderKind` and the discriminator switch silently falls through).
 *
 * The fix (mirroring `parseToolCard`): one `z.unknown().transform` per
 * `render_kind`, a `RENDER_KIND_SCHEMAS` registry keyed by the
 * discriminator, and a single `parseToolResult(raw)` entry point that
 * reads the discriminator tolerant of both spellings (`dualKey`) and
 * emits the CANONICAL snake_case envelope. The payload sub-tree is run
 * through `toSnakeDeep` so it converges to one shape regardless of which
 * transport delivered it.
 *
 * Why snake_case output (not camelCase props): the canonical convention
 * for opaque BE-owned payloads is snake_case (matches Pydantic + the SSE
 * source of truth + `tool-cards/schemas.ts`). The consumer
 * (`toolResultDispatcher.tsx`) maps the canonical payload onto the
 * camelCase primitive props with `toCamelDeep` at the render boundary,
 * where the citation-enrichment / deep-link / status-pill business logic
 * also lives (that logic depends on the turn's citation list, so it
 * cannot live in a pure codec).
 *
 * Failure mode: `parseToolResult` returns `null` on a missing/unknown
 * `render_kind`. The dispatcher logs + skips — a single malformed
 * tool-result never poisons the whole turn.
 */
import { z } from "zod";

import { dualKey, isObject, toSnakeDeep } from "@/api/codec";

// ---------------------------------------------------------------------
// Canonical output union — snake_case envelope, payload kept opaque.
//
// The payload shapes themselves are structurally varied (a data_grid
// has columns/rows, a key_value has fields[], a scheduling_plan has
// vendors/buildings, ...) and the render-boundary in the dispatcher
// narrows them per kind. We type `payload` as `unknown` here on purpose:
// the codec's job is to fix the ENVELOPE (discriminator + transport
// drift), not to re-validate every leaf — that would duplicate the
// structural narrowing the render functions already do, and the
// dispatcher's defensive contract (return null on shape miss) is the
// backstop.
// ---------------------------------------------------------------------

export const TOOL_RESULT_RENDER_KINDS = [
  "data_grid",
  "key_value",
  "prose",
  "deadline_list",
  "status_pill_matrix",
  "scheduling_plan",
  "scheduling_success",
  // LL87 chat-intro amendment (FR-CI-7) — the REQUEST flow's plan +
  // success envelopes. Registering here is the LOAD-BEARING codec step:
  // an unregistered kind makes parseToolResult return null and BOTH SSE
  // clients' done-filters silently drop the envelope (§3.9 trap #1).
  "ll87_intro_plan",
  "ll87_intro_success",
  "row_explain_thread",
  "clarify_question",
  "freetext_carbon_answer",
] as const;

export type ToolResultRenderKind = (typeof TOOL_RESULT_RENDER_KINDS)[number];

export interface CanonicalToolResult {
  readonly render_kind: ToolResultRenderKind;
  readonly payload: unknown;
  readonly tool_name?: string;
}

// ---------------------------------------------------------------------
// Per-render_kind schemas.
//
// Each schema reads the envelope's `payload` + `tool_name` tolerant of
// either spelling and emits the canonical snake_case payload sub-tree.
// `toSnakeDeep` collapses a camelized payload (replay path) back to the
// snake shape the SSE path delivers, so the two transports converge on
// ONE structure. `prose` is special-cased because its payload is a flat
// `{markdown}` that has no snake/camel ambiguity.
// ---------------------------------------------------------------------

const readToolName = (raw: Record<string, unknown>): string | undefined => {
  const v = dualKey(raw, "tool_name");
  return typeof v === "string" ? v : undefined;
};

const canonicalEnvelope = (
  raw: Record<string, unknown>,
  renderKind: ToolResultRenderKind,
): CanonicalToolResult => {
  const payload = dualKey(raw, "payload");
  const toolName = readToolName(raw);
  return {
    render_kind: renderKind,
    // The payload may have ridden the axios camelizer (replay path) —
    // toSnakeDeep collapses it back to the canonical snake shape. On
    // the SSE path the payload is already snake_case and toSnakeDeep is
    // a no-op. `undefined`/non-object payloads pass through (the
    // dispatcher's render fns null-out on a shape miss).
    payload: isObject(payload) || Array.isArray(payload)
      ? toSnakeDeep(payload)
      : payload,
    ...(toolName !== undefined ? { tool_name: toolName } : {}),
  };
};

type RenderKindParser = (
  raw: Record<string, unknown>,
) => CanonicalToolResult;

const RENDER_KIND_SCHEMAS: Record<ToolResultRenderKind, RenderKindParser> = {
  data_grid: (raw) => canonicalEnvelope(raw, "data_grid"),
  key_value: (raw) => canonicalEnvelope(raw, "key_value"),
  prose: (raw) => canonicalEnvelope(raw, "prose"),
  deadline_list: (raw) => canonicalEnvelope(raw, "deadline_list"),
  status_pill_matrix: (raw) => canonicalEnvelope(raw, "status_pill_matrix"),
  scheduling_plan: (raw) => canonicalEnvelope(raw, "scheduling_plan"),
  scheduling_success: (raw) => canonicalEnvelope(raw, "scheduling_success"),
  ll87_intro_plan: (raw) => canonicalEnvelope(raw, "ll87_intro_plan"),
  ll87_intro_success: (raw) => canonicalEnvelope(raw, "ll87_intro_success"),
  row_explain_thread: (raw) => canonicalEnvelope(raw, "row_explain_thread"),
  clarify_question: (raw) => canonicalEnvelope(raw, "clarify_question"),
  freetext_carbon_answer: (raw) => canonicalEnvelope(raw, "freetext_carbon_answer"),
};

/**
 * Resolve the discriminator from either snake_case (`render_kind`) or
 * camelCase (`renderKind`) — the boundary the axios interceptor breaks.
 */
const readRenderKind = (raw: unknown): string | null => {
  if (!isObject(raw)) return null;
  const v = dualKey(raw, "render_kind");
  return typeof v === "string" ? v : null;
};

/**
 * Parse a raw tool-result envelope (snake_case off SSE, camelCase off
 * the axios replay path) into a canonical snake_case
 * {@link CanonicalToolResult}. Returns `null` when the discriminator is
 * missing or unknown — the dispatcher logs + skips.
 *
 * This is the SINGLE consumer-boundary normalizer for tool-result
 * widget envelopes. Don't write a second one. The dispatcher's deleted
 * `camelize` + 5 `normalize*Payload` shims are gone; the per-kind
 * render functions consume this canonical output and map it onto the
 * camelCase primitive props with `toCamelDeep`.
 */
export const parseToolResult = (raw: unknown): CanonicalToolResult | null => {
  const kind = readRenderKind(raw);
  if (!kind) return null;
  const parser = RENDER_KIND_SCHEMAS[kind as ToolResultRenderKind];
  if (!parser) {
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(
        `[tool-results-schemas] parseToolResult: unknown render_kind="${kind}"`,
      );
    }
    return null;
  }
  return parser(raw as Record<string, unknown>);
};

/**
 * Validation-only Zod wrapper for the codec runners (`codecGet`). The
 * replay path feeds each persisted tool-result through `parseToolResult`
 * already; this schema lets a `ReplayMessage` codec compose it.
 */
export const toolResultSchema = z
  .unknown()
  .transform((raw, ctx): CanonicalToolResult => {
    const parsed = parseToolResult(raw);
    if (!parsed) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "tool_result: missing or unknown render_kind",
      });
      return z.NEVER;
    }
    return parsed;
  });

/** Test helper — kept named so lint can scope `render_kind` access. */
export const testReadRenderKind = readRenderKind;
